import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../../services/apiClient';
import ConfirmModal from '../../../components/common/ConfirmModal';
import Spinner from '../../../components/ui/Spinner';
import EmptyState from '../../../components/ui/EmptyState';
import Breadcrumb from '../../../components/ui/Breadcrumb';
import type { PaginatedResponse } from '../../../types/api';

interface Supplier {
    id: number; rnc: string; name: string; commercial_name: string | null;
    phone: string | null; email: string | null; is_active: boolean;
}

const SupplierListPage: React.FC = () => {
    const navigate = useNavigate();
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    const load = async () => {
        setLoading(true);
        try {
            const q = new URLSearchParams({ search: search || '' });
            const res = await apiClient.get<PaginatedResponse<Supplier>>(`/suppliers?${q}`);
            setSuppliers(res.data.data ?? []);
        } catch { setSuppliers([]); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const requestDelete = (id: number) => setPendingDeleteId(id);

    const cancelDelete = () => {
        if (deleting) return;
        setPendingDeleteId(null);
    };

    const confirmDelete = async () => {
        if (pendingDeleteId == null) return;
        setDeleting(true);
        setDeleteError(null);
        const id = pendingDeleteId;
        setPendingDeleteId(null);
        try {
            await apiClient.delete(`/suppliers/${id}`);
            load();
        } catch {
            setDeleteError('Error al eliminar');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <>
            <div className="page-wrapper">
                <div className="content container-fluid">
                    <div className="page-header">
                        <div className="row">
                            <div className="col-sm-12">
                                <Breadcrumb items={[{ label: 'Inicio', to: '/admin-dashboard' }, { label: 'Proveedores' }]} />
                            </div>
                        </div>
                        <div className="row align-items-center">
                            <div className="col"><h4 className="page-title">Proveedores ({suppliers.length})</h4></div>
                            <div className="col-auto">
                                <button className="btn btn-primary" onClick={() => navigate('/suppliers/new')}>
                                    <i className="fa fa-plus me-1"></i> Nuevo
                                </button>
                            </div>
                        </div>
                    </div>
                    {deleteError && <div className="alert alert-danger">{deleteError}</div>}
                    <div className="row mb-3">
                        <div className="col-md-4">
                            <input type="text" className="form-control form-control-sm" placeholder="Buscar..."
                                value={search} onChange={e => setSearch(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && load()} />
                        </div>
                        <div className="col-md-2">
                            <button className="btn btn-sm btn-outline-secondary" onClick={load}>
                                <i className="fa fa-search"></i>
                            </button>
                        </div>
                    </div>
                    <div className="card">
                        <div className="card-body">
                            {loading ? <Spinner variant="full-page" text="Cargando..." size="md" /> :
                             suppliers.length === 0 ? (
                                <EmptyState
                                    icon="fa-truck"
                                    title="No hay proveedores registrados"
                                    action={{ label: 'Nuevo Proveedor', to: '/suppliers/new', icon: 'fa-plus' }}
                                />
                             ) :
                             <div className="table-responsive"><table className="table table-hover">
                                <thead><tr><th>RNC</th><th>Nombre</th><th>Teléfono</th><th>Email</th><th>Acciones</th></tr></thead>
                                <tbody>{suppliers.map(s => (
                                    <tr key={s.id}>
                                        <td>{s.rnc}</td>
                                        <td><strong>{s.name}</strong>{s.commercial_name && <><br/><small className="text-muted">{s.commercial_name}</small></>}</td>
                                        <td>{s.phone || '-'}</td>
                                        <td>{s.email || '-'}</td>
                                        <td>
                                            <button className="btn btn-sm btn-outline-primary me-1" onClick={() => navigate(`/edit-supplier?id=${s.id}`)} aria-label="Editar">
                                                <i className="fa fa-pencil"></i></button>
                                            <button className="btn btn-sm btn-outline-danger" onClick={() => requestDelete(s.id)} aria-label="Eliminar">
                                                <i className="fa fa-trash"></i></button>
                                        </td>
                                    </tr>
                                ))}</tbody>
                             </table></div>}
                        </div>
                    </div>
                </div>
            </div>

            <ConfirmModal
                show={pendingDeleteId !== null}
                title="Eliminar proveedor"
                body="¿Está seguro que desea eliminar este proveedor? Esta acción no se puede deshacer."
                confirmLabel="Eliminar"
                cancelLabel="Cancelar"
                confirmVariant="danger"
                loading={deleting}
                onConfirm={confirmDelete}
                onCancel={cancelDelete}
            />
        </>
    );
};

export default SupplierListPage;
